import React, { useState, useRef, useEffect } from 'react';
import { HiOutlinePencil, HiOutlineCheck, HiOutlineXMark, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import { getLiveSupport, updateLiveSupport } from '../../services/apiService';

const DAYS_OF_WEEK = [
  { key: 'sunday', label: 'Sunday' },
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
];

const EMPTY_DATA = {
  airmeetSupportLink: '',
  startTime: '',
  endTime: '',
  daysOfWeek: [],
  messageWhenUnavailable: '',
};

function fromApi(res) {
  const d = res?.liveSupportData?.data?.attributes || res?.data?.attributes || res?.data || res || {};
  const days = d.daysOfWeek || {};
  return {
    airmeetSupportLink: d.airmeetSupportLink || '',
    startTime: (d.startTime || '').slice(0, 5),
    endTime: (d.endTime || '').slice(0, 5),
    messageWhenUnavailable: d.messageWhenUnavailable || '',
    daysOfWeek: Object.keys(days).filter((k) => k !== 'id' && days[k]),
  };
}

function toApi(uiData) {
  const daysOfWeek = {};
  DAYS_OF_WEEK.forEach(({ key }) => {
    daysOfWeek[key] = uiData.daysOfWeek.includes(key);
  });
  return {
    data: {
      airmeetSupportLink: uiData.airmeetSupportLink,
      startTime: `${uiData.startTime}:00.000`,
      endTime: `${uiData.endTime}:00.000`,
      messageWhenUnavailable: uiData.messageWhenUnavailable,
      daysOfWeek,
    },
  };
}

function parseMessage(str) {
  const parts = (str || '').split(' || ').map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return { header: '', points: [] };
  return { header: parts[0], points: parts.slice(1) };
}

function serializeMessage({ header, points }) {
  return [header, ...points].filter(Boolean).join(' || ');
}

const cardStyle = {
  background: 'var(--card-background, #fff)',
  border: '1px solid var(--border-main, #E5E7EB)',
  borderRadius: 8,
  padding: 24,
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-muted, #6B7280)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 6,
};

const inputStyle = {
  width: '100%',
  fontSize: 14,
  color: 'var(--text-primary, #111827)',
  border: '1px solid var(--border-main, #E5E7EB)',
  borderRadius: 6,
  padding: '8px 12px',
  outline: 'none',
  background: 'var(--card-background, #fff)',
  boxSizing: 'border-box',
};

function WidgetPreview({ header, points }) {
  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        border: '1px solid #E6E6E6',
        padding: 24,
        width: '100%',
        maxWidth: 420,
        position: 'relative',
      }}
    >
      {/* Close button mock */}
      <div
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          width: 20,
          height: 20,
          backgroundColor: '#FBFBFB',
          border: '1px solid rgba(0,0,0,0.06)',
          borderRadius: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          color: '#9CA3AF',
        }}
      >
        ×
      </div>

      {/* Inner gradient card */}
      <div
        style={{
          background: 'linear-gradient(180deg, rgba(255,247,249,0) 0%, rgba(255,242,246,1) 100%)',
          borderRadius: 8,
          padding: '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              backgroundColor: '#FFE9EE',
              borderRadius: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: 16,
            }}
          >
            ?
          </div>
          <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 16, fontWeight: 600, color: '#000' }}>
            Need Support?
          </span>
        </div>

        {/* Body */}
        <div>
          {header && (
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600, color: '#373737', margin: '0 0 8px 0' }}>
              {header}
            </p>
          )}
          {points.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {points.map((pt, i) => (
                <p key={i} style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: '#373737', lineHeight: 1.5, margin: 0 }}>
                  • {pt}
                </p>
              ))}
            </div>
          )}
          {!header && points.length === 0 && (
            <p style={{ color: '#9CA3AF', fontSize: 13, margin: 0 }}>No message configured</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SupportSettings() {
  const [data, setData] = useState(EMPTY_DATA);
  const [editData, setEditData] = useState(EMPTY_DATA);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [msgHeader, setMsgHeader] = useState('');
  const [msgPoints, setMsgPoints] = useState([]);
  const [editingPointIdx, setEditingPointIdx] = useState(null);
  const [editingPointValue, setEditingPointValue] = useState('');
  const [isAddingPoint, setIsAddingPoint] = useState(false);
  const [newPointDraft, setNewPointDraft] = useState('');

  const dragIndex = useRef(null);
  const dragOverIndex = useRef(null);

  useEffect(() => {
    getLiveSupport()
      .then((res) => {
        const uiData = fromApi(res);
        setData(uiData);
      })
      .catch(() => toast.error('Failed to load support settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleEdit = () => {
    const { header, points } = parseMessage(data.messageWhenUnavailable);
    setMsgHeader(header);
    setMsgPoints(points);
    setEditData({ ...data });
    setEditingPointIdx(null);
    setIsAddingPoint(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingPointIdx(null);
    setIsAddingPoint(false);
  };

  const handleSave = async () => {
    const messageWhenUnavailable = serializeMessage({ header: msgHeader, points: msgPoints });
    const uiData = { ...editData, messageWhenUnavailable };
    setSaving(true);
    try {
      await updateLiveSupport(toApi(uiData));
      setData(uiData);
      setIsEditing(false);
      setEditingPointIdx(null);
      setIsAddingPoint(false);
      toast.success('Support settings saved');
    } catch {
      toast.error('Failed to save support settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day) => {
    setEditData((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day],
    }));
  };

  // Point CRUD
  const handleStartEditPoint = (idx) => {
    setEditingPointIdx(idx);
    setEditingPointValue(msgPoints[idx]);
  };
  const handleSavePoint = (idx) => {
    if (editingPointValue.trim()) {
      setMsgPoints((prev) => prev.map((p, i) => (i === idx ? editingPointValue.trim() : p)));
    }
    setEditingPointIdx(null);
  };
  const handleCancelEditPoint = () => {
    setEditingPointIdx(null);
  };
  const handleDeletePoint = (idx) => {
    setMsgPoints((prev) => prev.filter((_, i) => i !== idx));
    if (editingPointIdx === idx) setEditingPointIdx(null);
  };
  const handleAddPoint = () => {
    if (newPointDraft.trim()) {
      setMsgPoints((prev) => [...prev, newPointDraft.trim()]);
      setNewPointDraft('');
      setIsAddingPoint(false);
    }
  };

  // Drag-and-drop reorder
  const handleDragStart = (idx) => {
    dragIndex.current = idx;
  };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === idx) return;
    dragOverIndex.current = idx;
    setMsgPoints((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(dragIndex.current, 1);
      updated.splice(idx, 0, moved);
      dragIndex.current = idx;
      return updated;
    });
  };
  const handleDragEnd = () => {
    dragIndex.current = null;
    dragOverIndex.current = null;
  };

  const viewedMessage = parseMessage(data.messageWhenUnavailable);

  if (loading) {
    return <p style={{ color: 'var(--text-muted, #6B7280)', fontSize: 14, padding: 24 }}>Loading…</p>;
  }

  return (
    <div>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary, #111827)', margin: 0 }}>
            Support Settings
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted, #6B7280)', marginTop: 4, marginBottom: 0 }}>
            Configure live support link, availability hours, and the message shown when support is offline
          </p>
        </div>
        {!isEditing ? (
          <button
            onClick={handleEdit}
            style={{ fontSize: 13, fontWeight: 600, color: '#fff', backgroundColor: '#4F46E5', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <HiOutlinePencil size={14} /> Edit
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleCancel}
              disabled={saving}
              style={{ fontSize: 13, fontWeight: 600, color: '#374151', backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <HiOutlineXMark size={14} /> Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ fontSize: 13, fontWeight: 600, color: '#fff', backgroundColor: '#059669', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <HiOutlineCheck size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Settings cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Support Link */}
        <div style={cardStyle}>
          <p style={labelStyle}>Support Link</p>
          {isEditing ? (
            <input
              type="url"
              value={editData.airmeetSupportLink}
              onChange={(e) => setEditData((prev) => ({ ...prev, airmeetSupportLink: e.target.value }))}
              style={inputStyle}
              placeholder="https://zoom.us/j/..."
            />
          ) : (
            <a href={data.airmeetSupportLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: '#4F46E5', textDecoration: 'none', wordBreak: 'break-all' }}>
              {data.airmeetSupportLink}
            </a>
          )}
        </div>

        {/* Availability Hours */}
        <div style={cardStyle}>
          <p style={labelStyle}>Availability Hours</p>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted, #9CA3AF)', display: 'block', marginBottom: 4 }}>Start Time</span>
              {isEditing ? (
                <input type="time" value={editData.startTime} onChange={(e) => setEditData((prev) => ({ ...prev, startTime: e.target.value }))} style={{ ...inputStyle, width: 'auto' }} />
              ) : (
                <span style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary, #111827)' }}>{data.startTime}</span>
              )}
            </div>
            <span style={{ fontSize: 20, color: 'var(--text-muted, #D1D5DB)', fontWeight: 300 }}>–</span>
            <div style={{ flex: '1 1 140px' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted, #9CA3AF)', display: 'block', marginBottom: 4 }}>End Time</span>
              {isEditing ? (
                <input type="time" value={editData.endTime} onChange={(e) => setEditData((prev) => ({ ...prev, endTime: e.target.value }))} style={{ ...inputStyle, width: 'auto' }} />
              ) : (
                <span style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary, #111827)' }}>{data.endTime}</span>
              )}
            </div>
          </div>
        </div>

        {/* Days of Week */}
        <div style={cardStyle}>
          <p style={labelStyle}>Active Days</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {DAYS_OF_WEEK.map(({ key, label }) => {
              const isActive = isEditing ? editData.daysOfWeek.includes(key) : data.daysOfWeek.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => isEditing && toggleDay(key)}
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    padding: '6px 14px',
                    borderRadius: 999,
                    border: '1px solid',
                    borderColor: isActive ? '#4F46E5' : 'var(--border-main, #E5E7EB)',
                    backgroundColor: isActive ? 'rgba(79,70,229,0.08)' : 'transparent',
                    color: isActive ? '#4F46E5' : 'var(--text-muted, #9CA3AF)',
                    cursor: isEditing ? 'pointer' : 'default',
                    transition: 'all 150ms ease',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Message When Unavailable */}
        <div style={cardStyle}>
          <p style={labelStyle}>Message When Unavailable</p>

          {isEditing ? (
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {/* Editor */}
              <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Header field */}
                <div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted, #6B7280)', display: 'block', marginBottom: 6 }}>
                    Section Header
                  </span>
                  <input
                    type="text"
                    value={msgHeader}
                    onChange={(e) => setMsgHeader(e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. Live Support Timing"
                  />
                </div>

                {/* Points list */}
                <div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted, #6B7280)', display: 'block', marginBottom: 8 }}>
                    Bullet Points
                    <span style={{ fontWeight: 400, marginLeft: 6, color: '#9CA3AF' }}>(drag to reorder)</span>
                  </span>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {msgPoints.map((pt, idx) => (
                      <div
                        key={idx}
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDragEnd={handleDragEnd}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 10px',
                          borderRadius: 6,
                          border: '1px solid var(--border-main, #E5E7EB)',
                          backgroundColor: 'var(--card-background, #fff)',
                          cursor: 'grab',
                        }}
                      >
                        {/* Drag handle */}
                        <span style={{ color: '#9CA3AF', fontSize: 16, cursor: 'grab', userSelect: 'none', flexShrink: 0 }}>⠿</span>

                        {editingPointIdx === idx ? (
                          <input
                            type="text"
                            value={editingPointValue}
                            onChange={(e) => setEditingPointValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSavePoint(idx); if (e.key === 'Escape') handleCancelEditPoint(); }}
                            style={{ ...inputStyle, padding: '4px 8px', flex: 1 }}
                            autoFocus
                          />
                        ) : (
                          <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary, #111827)' }}>{pt}</span>
                        )}

                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          {editingPointIdx === idx ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleSavePoint(idx)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#059669', padding: 4 }}
                                title="Save"
                              >
                                <HiOutlineCheck size={15} />
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEditPoint}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4 }}
                                title="Cancel"
                              >
                                <HiOutlineXMark size={15} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => handleStartEditPoint(idx)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4F46E5', padding: 4 }}
                                title="Edit"
                              >
                                <HiOutlinePencil size={15} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeletePoint(idx)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}
                                title="Delete"
                              >
                                <HiOutlineTrash size={15} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Add point row */}
                    {isAddingPoint ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 10px', borderRadius: 6, border: '1px dashed #4F46E5', backgroundColor: 'rgba(79,70,229,0.03)' }}>
                        <input
                          type="text"
                          value={newPointDraft}
                          onChange={(e) => setNewPointDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleAddPoint(); if (e.key === 'Escape') { setIsAddingPoint(false); setNewPointDraft(''); } }}
                          style={{ ...inputStyle, padding: '4px 8px', flex: 1 }}
                          placeholder="e.g. Admin Helpdesk , Monday – Friday , 12:00 PM – 7:00 PM"
                          autoFocus
                        />
                        <button type="button" onClick={handleAddPoint} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#059669', padding: 4 }} title="Add">
                          <HiOutlineCheck size={15} />
                        </button>
                        <button type="button" onClick={() => { setIsAddingPoint(false); setNewPointDraft(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4 }} title="Cancel">
                          <HiOutlineXMark size={15} />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsAddingPoint(true)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#4F46E5', background: 'none', border: '1px dashed #4F46E5', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', width: 'fit-content' }}
                      >
                        <HiOutlinePlus size={14} /> Add Point
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Live Preview */}
              <div style={{ flex: '0 0 auto', minWidth: 280 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted, #6B7280)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Preview
                </span>
                <WidgetPreview header={msgHeader} points={msgPoints} />
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {/* Serialized string view */}
              <div style={{ flex: '1 1 260px' }}>
                <p style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>Stored format</p>
                <p style={{ fontSize: 13, color: 'var(--text-primary, #111827)', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0, wordBreak: 'break-word' }}>
                  {data.messageWhenUnavailable}
                </p>
              </div>
              {/* Preview */}
              <div style={{ flex: '0 0 auto', minWidth: 280 }}>
                <p style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>Widget preview</p>
                <WidgetPreview header={viewedMessage.header} points={viewedMessage.points} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
